import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ResponsiveChartContainer,
  LinePlot,
  BarPlot,
  MarkPlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsTooltip,
  ChartsAxisHighlight,
} from "@mui/x-charts";

import { formatCurrency, theme } from "../../../utils";

type ChartPoint = {
  month: string;
  washes: number;
  revenue: number;
};

export type RevenueTrendChartProps = {
  data: ChartPoint[];
};

const LEGEND_ITEMS = [
  {
    label: "Revenue",
    color: theme.palette.accent.main,
  },
  {
    label: "Washes",
    color: theme.palette.primary.main,
  },
];

export const RevenueTrendChart = ({ data }: RevenueTrendChartProps) => {
  const isMobile = useMediaQuery("(max-width:600px)");

  const revenueData = data.map((item) => item.revenue);
  const washesData = data.map((item) => item.washes);
  const labels = data.map((item) => item.month);

  const maxRevenue = Math.max(...revenueData, 0);
  const maxWashes = Math.max(...washesData, 0);

  const formatRevenueAxisValue = (value: number) => {
    if (!isMobile) {
      return formatCurrency(value);
    }

    if (Math.abs(value) >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(
        value % 1_000_000 === 0 ? 0 : 1,
      )}M`;
    }

    if (Math.abs(value) >= 1_000) {
      return `$${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
    }

    return `$${Math.round(value)}`;
  };

  return (
    <Box
      sx={{
        minHeight: { xs: 420, sm: 500 },
        height: "auto",
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        borderRadius: "18px",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 12px rgba(0,0,0,.08)",
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 18, sm: 24 },
          fontWeight: 700,
        }}
      >
        Billing Metrics
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          mb: 2,
          fontSize: 14,
          color: "#94A3B8",
        }}
      >
        invoicing totals and progress
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: 1.5, sm: 3 },
          mb: { xs: 1.5, sm: 2 },
          minHeight: 24,
        }}
      >
        {LEGEND_ITEMS.map((item) => (
          <Box
            key={item.label}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: "#475569",
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            {item.label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          height: { xs: 300, sm: 360 },
          minWidth: 0,
        }}
      >
        <ResponsiveChartContainer
          series={
            isMobile
              ? [
                  {
                    type: "bar",
                    id: "revenue",
                    label: "Revenue",
                    color: theme.palette.accent.main,
                    data: revenueData,
                    yAxisId: "revenue-axis",
                    valueFormatter: (value) =>
                      value != null ? formatCurrency(value) : "",
                  },
                  {
                    type: "bar",
                    id: "washes",
                    label: "Washes",
                    color: theme.palette.primary.main,
                    data: washesData,
                    yAxisId: "washes-axis",
                  },
                ]
              : [
                  {
                    type: "line",
                    id: "revenue",
                    label: "Revenue",
                    color: theme.palette.accent.main,
                    data: revenueData,
                    yAxisId: "revenue-axis",
                    valueFormatter: (value) =>
                      value != null ? formatCurrency(value) : "",
                  },
                  {
                    type: "line",
                    id: "washes",
                    label: "Washes",
                    color: theme.palette.primary.main,
                    data: washesData,
                    yAxisId: "washes-axis",
                  },
                ]
          }
          xAxis={[
            {
              id: "months",
              scaleType: "band",
              data: labels,
            },
          ]}
          yAxis={[
            {
              id: "revenue-axis",
              position: "left",
              min: 0,
              max: maxRevenue * 1.1,
              valueFormatter: formatRevenueAxisValue,
            },
            {
              id: "washes-axis",
              position: "right",
              min: 0,
              max: maxWashes * 1.1,
            },
          ]}
          margin={{
            left: isMobile ? 62 : 90,
            right: isMobile ? 34 : 90,
            top: 20,
            bottom: isMobile ? 42 : 54,
          }}
        >
          <ChartsGrid />
          <ChartsAxisHighlight x="line" />

          {isMobile ? (
            <BarPlot />
          ) : (
            <>
              <LinePlot />
              <MarkPlot />
            </>
          )}

          <ChartsTooltip />
          <ChartsXAxis />
          <ChartsYAxis axisId="revenue-axis" />
          <ChartsYAxis axisId="washes-axis" />
        </ResponsiveChartContainer>
      </Box>
    </Box>
  );
};
